// src/components/subcontractors/modals/PaymentModal.jsx
import { useState, useEffect, useRef } from 'react';
import { useSubcontractors } from '../../../context/SubcontractorContext';

const METHODS = ['zelle', 'venmo', 'cash', 'check', 'wire', 'other'];

function getRecordedBy() {
  try {
    const p = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
    return p.displayName || 'Staff';
  } catch { return 'Staff'; }
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function PaymentModal({ job, onClose }) {
  const { addPayment, getJobPaymentSummary } = useSubcontractors();
  const summary = getJobPaymentSummary(job);

  const [form, setForm] = useState({
    amount:    summary.amountOwed > 0 ? String(summary.amountOwed) : '',
    method:    'zelle',
    date:      today(),
    reference: '',
    notes:     '',
  });
  const [errors, setErrors] = useState({});

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: undefined }));
  }

  function validate() {
    const e = {};
    const amt = Number(form.amount);
    if (!form.amount || isNaN(amt) || amt <= 0) e.amount = 'Enter a valid amount greater than 0';
    if (!form.date)   e.date = 'Date is required';
    return e;
  }

  function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    addPayment(job.id, {
      amount:    Number(form.amount),
      method:    form.method,
      date:      new Date(form.date).toISOString(),
      reference: form.reference.trim() || '',
      notes:     form.notes.trim() || '',
      recordedBy: getRecordedBy(),
    });
    onClose();
  }

  // Stable Escape handler
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const inputCls = 'w-full bg-white dark:bg-[#0F1923] border border-gray-200 dark:border-[#243348] rounded-lg px-3 py-2 text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0]';
  const labelCls = 'block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="bg-white dark:bg-[#111C27] border border-gray-200 dark:border-[#1E3048] rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-[#1E3048]">
          <h2 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Record Payment</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Job context */}
          <div className="bg-gray-100 dark:bg-[#0F1923] rounded-lg px-4 py-3 text-[var(--wm-text-body)]">
            <p className="text-[#0F1923] dark:text-[#F8FAFE] font-medium">{job.projectRef} — {job.customerName}</p>
            <p className="text-[#64748B] dark:text-[#7D93AE] text-[var(--wm-text-ui)] mt-0.5">{job.vehicleLabel}</p>
            <div className="flex gap-4 mt-2 text-[var(--wm-text-ui)]">
              <span className="text-[#10B981]">Paid: ${summary.amountPaid.toLocaleString()}</span>
              <span className="text-[#F59E0B]">Owed: ${summary.amountOwed.toLocaleString()}</span>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className={labelCls}>Amount <span className="text-red-400">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] dark:text-[#7D93AE] text-[var(--wm-text-body)]">$</span>
              <input
                className={inputCls + ' pl-6'}
                type="number" min="0.01" step="0.01"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                placeholder="0.00"
                autoFocus
              />
            </div>
            {errors.amount && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.amount}</p>}
            {!errors.amount && Number(form.amount) > summary.amountOwed && summary.amountOwed > 0 && (
              <p className="text-[var(--wm-text-ui)] text-[#F59E0B] mt-1">
                Amount exceeds balance owed (${summary.amountOwed.toLocaleString()})
              </p>
            )}
          </div>

          {/* Method */}
          <div>
            <label className={labelCls}>Payment Method <span className="text-red-400">*</span></label>
            <div className="flex flex-wrap gap-2">
              {METHODS.map(m => (
                <button
                  key={m}
                  onClick={() => set('method', m)}
                  className="px-3 py-1 rounded-full text-[var(--wm-text-ui)] font-medium border capitalize transition-colors"
                  style={{
                    background:  form.method === m ? '#2E8BF022' : 'transparent',
                    borderColor: form.method === m ? '#2E8BF0'   : '#243348',
                    color:       form.method === m ? '#2E8BF0'   : '#7D93AE',
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className={labelCls}>Date <span className="text-red-400">*</span></label>
            <input className={inputCls} type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            {errors.date && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.date}</p>}
          </div>

          {/* Reference */}
          <div>
            <label className={labelCls}>Reference # <span className="text-[#64748B] dark:text-[#4A6380]">(optional)</span></label>
            <input className={inputCls} value={form.reference} onChange={e => set('reference', e.target.value)} placeholder="#4821, CHK-001, etc." />
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>Notes <span className="text-[#64748B] dark:text-[#4A6380]">(optional)</span></label>
            <input className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Deposit, final payment, etc." />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-[#1E3048]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">Cancel</button>
          <button onClick={handleSave} className="px-5 py-2 bg-[#10B981] hover:bg-[#059669] text-white text-[var(--wm-text-body)] font-medium rounded-lg transition-colors">
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
}
